import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as DotCircle } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as ProfileIcon } from '../../assets/icons/profile.svg';
import { ReactComponent as RfidIcon } from '../../assets/icons/side-panel/user-side-panel/rfid-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import CarImage from '../../assets/images/side-panel/carpng.png';
import BrandImage from '../../assets/images/side-panel/dummy_car_icon.png';
import DefaultApiLimit from '../../config/defaultLimit';
import { ApiRequest } from '../../interface/common.interface';
import { PersonalChargersProperties } from '../../interface/end-consumer.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchEndConsumerActivityLogs,
  fetchPersonalChargers,
  setEndConsumerPage,
  setSidepanelSortData,
} from '../../redux/slices/EndConsumerManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import endConsumer from '../../utils/api/end-consumer';
import CustomTable from '../custom-table/CustomTable';

import ChargerSidePanel from './ChargerSidePanel';

interface UserInfo {
  name?: string;
  phoneNumber?: string;
  email?: string;
  profileImage?: string;
}

interface VehicleInfo {
  manufacturer: string;
  model: string;
  isSelected: boolean;
  connectorType: string;
  batteryCapacity: string;
  brandImageUrl?: string;
  vehicleImageUrl?: string;
  defaultVehicleImageUrl?: string;
}

interface SessionInfo {
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
}

interface UserDetailsProperties {
  userInfo: UserInfo;
  rfidNo: string[]; // assuming RFID numbers are strings
  vehicleInfo: VehicleInfo[];
  sessionInfo: SessionInfo[];
  paidAmount: number;
}

interface EndConsumerSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/* interface AvailableTabProperties {
  label: string;
  key: string;
  isActive: boolean;
} */

/**
 * view error image
 * @param event
 */
const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>, text: string) => {
  const target = event.currentTarget;
  target.src = text === 'car' ? CarImage : BrandImage; // fallback image
};

/**
 * End consumer side panel
 * @returns
 */
export default function EndConsumerSidePanel(properties: EndConsumerSidePanelProperties) {
  const { t, i18n } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [selectedCharger, setSelectedCharger] = useState('');
  const [overlaySidePanel, setOverlaySidePanel] = useState<'open' | 'closed'>('closed');
  const {
    activityLogs,
    personalChargers,
    sidePanelSortData: sortBy,
    sidePanelSortField: sortField,
    endConsumerTotalResults: totalResults,
    endConsumerTotalPages: totalPages,
    endConsumerLimit: limit,
    endConsumerPage: page,
  } = useAppSelector((state) => state.endConsumer);
  /* State to track window height for adaptive layout */
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const [userDetails, setUserDetails] = useState<UserDetailsProperties>({
    userInfo: {},
    rfidNo: [],
    vehicleInfo: [],
    sessionInfo: [],
    paidAmount: 0,
  });

  /**
   * Function to handle pagination
   * @param value
   */
  const pageNateLimitChange = (value: ApiRequest) => {
    dispatch(setEndConsumerPage(value));
    let userCustomId;
    if (fetchIdFrom === 'queryParams') {
      userCustomId = searchParameters.get('userCustomId');
    } else if (fetchIdFrom === 'props') {
      userCustomId = id;
    }

    if (!userCustomId) {
      return;
    }
    dispatch(
      fetchPersonalChargers({
        id: userCustomId,
        page: value?.page || 1,
        limit: value?.limit || 10,
        sortField,
        sortBy,
      }),
    );
  };

  const detailsTabs = useMemo(
    () => [
      {
        label: t('profile'),
        key: 'profile',
        isActive: true,
      },
      {
        label: t('chargers'),
        key: 'chargers',
        isActive: false,
      },
    ],
    [t],
  );

  const [activeTabKey, setActiveTabKey] = useState('profile');

  const tabsWithActiveStatus = useMemo(
    () => detailsTabs.map((tab: any) => ({ ...tab, isActive: tab.key === activeTabKey })),
    [detailsTabs, activeTabKey],
  );

  /**
   * Function to fetch userInfo
   * @param userCustomId
   */
  const handleFetchUserInfo = async (userCustomId: string) => {
    endConsumer
      .fetchUserInfoById(userCustomId)
      .then((response) => {
        setUserDetails((previousDetails) => ({
          ...previousDetails,
          userInfo: {
            name: `${response?.data?.firstName || ''} ${response?.data?.lastName || ''}`,
            phoneNumber: response?.data?.phoneNumber || '',
            email: response?.data?.email || '',
            profileImage: response?.data?.profileImage?.length
              ? response?.data?.profileImage[0]?.signedUrl
              : '',
          },
        }));
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to fetch userInfo
   * @param userCustomId
   */
  const handleFetchVehicleDetails = async (userCustomId: string) => {
    endConsumer
      .fetchVehicleDetailsByUserCustomId(userCustomId)
      .then((response) => {
        setUserDetails((previousDetails) => ({
          ...previousDetails,
          vehicleInfo: response?.data,
        }));
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to fetch userInfo
   * @param userCustomId
   */
  const handleTransactionDetails = async (userCustomId: string) => {
    endConsumer
      .fetchTransactionByUserCustomId(userCustomId)
      .then((response) => {
        const paidAmount = response?.data?.commercialTransaction?.reduce(
          (total: any, transaction: any) => total + (Number(transaction?.amount) || 0),
          0,
        );

        const sessionInfo: SessionInfo[] = [
          ...(response?.data?.commercialTransaction || []),
          ...(response?.data?.personalTransaction || []),
        ]?.map((session) => ({
          ...session,
          startTime: FormatUtcString(ConvertUTCtoHHMM(session?.transactionStartsAt || '')),
          endTime: FormatUtcString(ConvertUTCtoHHMM(session?.transactionEndsAt || '')),
          // startTime: ConvertUTCtoHHMM(session?.transactionStartsAt || ''),
          // endTime: ConvertUTCtoHHMM(session?.transactionEndsAt || ''),
          startDate: ConvertUTCtoDate(session?.transactionStartsAt || ''),
          endDate: ConvertUTCtoDate(session?.transactionEndsAt || ''),
        }));

        setUserDetails((previousDetails) => ({
          ...previousDetails,
          sessionInfo,
          paidAmount,
        }));
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to switch the tab
   * @param key
   */
  const handleSwitchTab = (key: string) => {
    setActiveTabKey(key);
  };

  const column = [
    {
      label: t('chargingSNo'),
      accessorKey: 'chargingPointSn' as keyof PersonalChargersProperties,
      sortAllow: true,
      /**
       * Custom data
       * @param row
       */
      customData: (rowData: PersonalChargersProperties) => (
        <p
          role="presentation"
          className="hyperlink"
          onClick={() => {
            setSelectedCharger(rowData.chargingPointSn);
            setOverlaySidePanel('open');
          }}>
          {rowData.chargingPointSn}
        </p>
      ),
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof PersonalChargersProperties,
      sortAllow: true,
    },
    {
      label: t('chargerStatus'),
      accessorKey: 'isAvailable' as keyof PersonalChargersProperties,
      sortAllow: true,
      /**
       * Rendering available status
       * @returns
       */
      customData: (rowData: PersonalChargersProperties) => (
        <p>{rowData.isAvailable ? t('online') : t('offline')}</p>
      ),
    },
  ];
  /**
   * Function to handle sort
   * @param value
   */
  const handleSortFunction = (value: any) => {
    let userCustomId;
    if (fetchIdFrom === 'queryParams') {
      userCustomId = searchParameters.get('userCustomId');
    } else if (fetchIdFrom === 'props') {
      userCustomId = id;
    }

    if (!userCustomId) {
      return;
    }
    dispatch(setSidepanelSortData(value));
    dispatch(
      fetchPersonalChargers({
        id: userCustomId,
        ...value,
        page,
        limit,
      }),
    );
  };
  useEffect(() => {
    let userCustomId;
    if (fetchIdFrom === 'queryParams') {
      userCustomId = searchParameters.get('userCustomId');
    } else if (fetchIdFrom === 'props') {
      userCustomId = id;
    }

    if (!userCustomId) {
      return;
    }

    dispatch(fetchEndConsumerActivityLogs(userCustomId));
    handleFetchUserInfo(userCustomId);
    handleFetchVehicleDetails(userCustomId);
    handleTransactionDetails(userCustomId);
    dispatch(
      fetchPersonalChargers({
        id: userCustomId,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      }),
    );
  }, [searchParameters, id]);

  useEffect(() => {
    /**
     * Function to handle window resize
     * @returns void
     */
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="end-consumer-side-panel-wrap" dir={i18n.dir()}>
      <div className="end-consumer-side-panel-tabs">
        {tabsWithActiveStatus.map((tab: any) => (
          <div
            key={tab.key}
            className={`each-tab ${tab.isActive ? 'active' : ''}`}
            role="presentation"
            onClick={() => handleSwitchTab(tab.key)}>
            <h6>{tab.label}</h6>
            <div className="bottom-green" />
          </div>
        ))}
      </div>
      <div
        className="end-consumer-side-panel-container"
        style={{
          height: `${windowHeight - 320}px`,
          overflowY: 'scroll',
        }}>
        {(() => {
          const activeTab = activeTabKey;
          if (activeTab === 'profile') {
            return (
              <>
                <div className="end-consumer-details">
                  <div className="end-consumer-profile-content">
                    {userDetails.userInfo?.profileImage ? (
                      <img
                        src={userDetails.userInfo?.profileImage || ''}
                        alt=""
                        className="profile-image"
                      />
                    ) : (
                      <ProfileIcon />
                    )}
                    <div className="end-consumer-content-details">
                      <h2>{userDetails.userInfo?.name}</h2>
                      <h6>{userDetails.userInfo?.email || userDetails.userInfo?.phoneNumber}</h6>
                      {/* Need to show sts here */}
                    </div>
                  </div>
                  <div className="block-user" />
                </div>
                <div className="user-info-wrap">
                  <div className="each-user-info">
                    <div className="header-icon-text">
                      <span>
                        <RfidIcon />
                      </span>
                      <h6>{t('rFIDNo')}</h6>
                    </div>
                    <p>-</p>
                  </div>
                  {userDetails.userInfo?.phoneNumber && (
                    <div className="each-user-info">
                      <div className="header-icon-text">
                        <span>
                          <RfidIcon />
                        </span>
                        <h6>{t('phoneNumber')}</h6>
                      </div>
                      <p>{userDetails.userInfo?.phoneNumber}</p>
                    </div>
                  )}
                  {userDetails.userInfo?.email && (
                    <div className="each-user-info">
                      <div className="header-icon-text">
                        <span>
                          <RfidIcon />
                        </span>
                        <h6>{t('email')}</h6>
                      </div>
                      <p>{userDetails.userInfo?.email}</p>
                    </div>
                  )}
                </div>
                <div className="vehicle-info-wrap">
                  <div className="header-icon-text">
                    <span>
                      <RfidIcon />
                    </span>
                    <h6>{t('vehicleInfo')}</h6>
                  </div>
                  <div className="vehicle-info-container">
                    {userDetails.vehicleInfo?.map((vehicle) => (
                      <div className="each-vehicle-info-container">
                        <div className="end-consumer-side-panel-card-view">
                          <div className="end-consumer-side-panel-card-details">
                            <h6>{t('brand')}</h6>
                            <p>{vehicle?.manufacturer}</p>
                          </div>
                          <div className="card-image-wrap">
                            {/* To-Do -> Need to configure images based on Model name */}
                            <img
                              src={vehicle?.brandImageUrl || BrandImage}
                              alt=""
                              className="card-image"
                              onError={(error) => handleImageError(error, 'brand')}
                            />
                          </div>
                        </div>
                        <div className="end-consumer-side-panel-card-view">
                          <div className="end-consumer-side-panel-card-details">
                            <h6>{t('model')}</h6>
                            <p>{vehicle?.model}</p>
                          </div>
                          <div className="card-image-wrap">
                            {/* To-Do -> Need to configure images based on Model name */}
                            <img
                              src={
                                vehicle?.vehicleImageUrl && vehicle?.vehicleImageUrl !== 'default'
                                  ? vehicle?.vehicleImageUrl
                                  : vehicle?.defaultVehicleImageUrl || CarImage
                              }
                              alt=""
                              className="card-image"
                              onError={(error) => handleImageError(error, 'car')}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="session-details-wrap">
                  <div className="header-icon-text">
                    <span>
                      <RfidIcon />
                    </span>
                    <h6>{t('sessionDetails')}</h6>
                  </div>
                  <div className="session-details-container">
                    {userDetails.sessionInfo?.map((session) => (
                      <div className="each-session-details-container">
                        <div className="end-consumer-side-panel-card-view">
                          <div className="end-consumer-side-panel-card-details">
                            <h6>{t('startTime')}</h6>
                            <p>{session.startTime}</p>
                          </div>
                        </div>
                        <div className="end-consumer-side-panel-card-view">
                          <div className="end-consumer-side-panel-card-details">
                            <h6>{t('endTime')}</h6>
                            <p>{session.endTime}</p>
                          </div>
                        </div>
                        <div className="end-consumer-side-panel-card-view">
                          <div className="end-consumer-side-panel-card-details">
                            <h6>{t('startDate')}</h6>
                            <p>{session.startDate}</p>
                          </div>
                        </div>
                        <div className="end-consumer-side-panel-card-view">
                          <div className="end-consumer-side-panel-card-details">
                            <h6>{t('endDate')}</h6>
                            <p>{session.endDate}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="paid-amount-wrap">
                  <div className="header-icon-text">
                    <span>
                      <RfidIcon />
                    </span>
                    <h6>{t('paidAmount')}</h6>
                  </div>
                  <p>{ToFixedValue(userDetails.paidAmount)}/-</p>
                </div>
                <div className="activity-logs-wrap">
                  <div className="header-icon-text">
                    <span>
                      <RfidIcon />
                    </span>
                    <h6>{t('activityLogs')}</h6>
                  </div>
                  <div className="active-log-content">
                    <div className="active-logs-list">
                      {activityLogs.map((activityLog: any, index) => (
                        <div className="active-loge-item">
                          <div className={`icon ${index !== activityLogs.length - 1 && 'line'}`}>
                            <DotCircle />
                          </div>
                          <div className="details">
                            <div className="calender-icon">
                              {/* {theme === 'light' ? <Calender /> : <CalenderDark />} */}
                            </div>
                            <div className="calender-details">
                              <div className="date">{activityLog?.date}</div>
                              <div className="time-list">
                                {activityLog?.logs?.map((value: any) => (
                                  <div className="time-item">
                                    <div className="title">
                                      {FormatUtcString(ConvertUTCtoHHMM(value?.createdAt))}
                                    </div>
                                    <div className="sub-title">
                                      {value?.title}
                                      {/* {value.status === 'unread' ? <div className="text-danger"><RemoveIcon /></div> : <div className="text-success">{ theme === 'light' ? <CheckCircle /> : <CheckCircleDark />}</div>} */}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {activityLogs.length === 0 && (
                        <div className="not-found">
                          <div className="title">{t('noActivityLogs')}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            );
          }

          return (
            <div className="end-consumer-chargers-table">
              <CustomTable
                header={column}
                data={personalChargers}
                serialNo
                limit={limit}
                page={page}
                totalPages={totalPages}
                totalResults={totalResults}
                sortData={{ sortBy, sortField }}
                handleSortChange={handleSortFunction}
                pageLimitChange={pageNateLimitChange}
                sizeChangeButtonRequired
              />
              <div
                className={`end-consumer-side-panel-view-wrap ${
                  overlaySidePanel !== 'closed' ? 'active' : ''
                }`}>
                <div className="side-panel-header">
                  <span style={{ opacity: '0.5', cursor: 'not-allowed' }}>
                    <NewTabIcon />
                  </span>
                  <span role="presentation" onClick={() => setOverlaySidePanel('closed')}>
                    <CloseIcon />
                  </span>
                </div>
                <div className="side-panel-content">
                  {selectedCharger && <ChargerSidePanel fetchIdFrom="props" id={selectedCharger} />}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
